import { useCallback } from "react"

import { mapTransportMetricToRecordData } from "@/metrics/catalog"
import {
  transport,
  useTransportMutation,
  useTransportQueryUtils,
} from "@/transport"
import { useQueryClient } from "@tanstack/react-query"

import { invalidateHomeQueries } from "@/lib/home-query-cache"
import { logger } from "@/lib/logger"
import type { ArchiveMetricRequest } from "@/types_api/backend/transport/archive_metric_request"
import type { CreateMetricRequest } from "@/types_api/backend/transport/create_metric_request"
import type { UpdateMetricRequest } from "@/types_api/backend/transport/update_metric_request"
import type { MetricRecordData } from "@/types/metrics/contracts"

const log = logger("MetricPersistenceTransport")

type PersistedMetricRecordData = MetricRecordData & { id: string }

type CreateMetricInput = Pick<
  MetricRecordData,
  | "description"
  | "metric_display_name"
  | "metric_manager"
  | "metric_metadata"
  | "output_type"
> & {
  category?: string | null
  metric_name?: string
}

type UpdateMetricInput = CreateMetricInput & {
  id: string
  tags?: UpdateMetricRequest["tags"]
}

function buildCreateMetricRequest(
  metric: CreateMetricInput,
): CreateMetricRequest {
  return {
    metric_name: metric.metric_name || undefined,
    metric_display_name: metric.metric_display_name,
    metric_metadata: { ...metric.metric_metadata },
    description: metric.description,
    metric_manager: metric.metric_manager,
    category: metric.category ?? null,
    output_type: metric.output_type ?? null,
  }
}

function buildUpdateMetricRequest(
  metric: UpdateMetricInput,
): UpdateMetricRequest {
  return {
    metric_id: metric.id,
    metric_name: metric.metric_name || undefined,
    metric_display_name: metric.metric_display_name,
    metric_metadata: { ...metric.metric_metadata },
    description: metric.description,
    metric_manager: metric.metric_manager,
    category: metric.category ?? null,
    output_type: metric.output_type ?? null,
    tags: metric.tags,
  }
}

export function useMetricPersistenceTransport() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const createMetricMutation = useTransportMutation(
    transport.operations.createMetric,
  )
  const duplicateMetricMutation = useTransportMutation(
    transport.operations.duplicateMetric,
  )
  const updateMetricMutation = useTransportMutation(
    transport.operations.updateMetric,
  )
  const archiveMetricMutation = useTransportMutation(
    transport.operations.archiveMetric,
  )
  const revertMetricVersionMutation = useTransportMutation(
    transport.operations.revertMetricVersion,
  )

  const invalidateMetrics = useCallback(
    () => transportQueryUtils.invalidate(transport.resources.metrics),
    [transportQueryUtils],
  )

  const invalidateRelatedQueries = useCallback(async () => {
    const results = await Promise.allSettled([
      invalidateMetrics(),
      transportQueryUtils.invalidate(transport.resources.agents),
      invalidateHomeQueries(queryClient),
    ])

    for (const result of results) {
      if (result.status === "rejected") {
        log.error("Metric cache invalidation failed", result.reason)
      }
    }
  }, [invalidateMetrics, queryClient, transportQueryUtils])

  const createMetric = useCallback(
    async (metric: CreateMetricInput): Promise<PersistedMetricRecordData> => {
      const response = await createMetricMutation.mutateAsync(
        buildCreateMetricRequest(metric),
      )
      await invalidateRelatedQueries()
      return mapTransportMetricToRecordData(response.metric)
    },
    [createMetricMutation, invalidateRelatedQueries],
  )

  const duplicateMetric = useCallback(
    async (metricId: string): Promise<PersistedMetricRecordData> => {
      const response = await duplicateMetricMutation.mutateAsync({
        metric_id: metricId,
      })
      await invalidateRelatedQueries()
      return mapTransportMetricToRecordData(response.metric)
    },
    [duplicateMetricMutation, invalidateRelatedQueries],
  )

  const updateMetric = useCallback(
    async (metric: UpdateMetricInput): Promise<PersistedMetricRecordData> => {
      const response = await updateMetricMutation.mutateAsync(
        buildUpdateMetricRequest(metric),
      )
      await invalidateRelatedQueries()
      return mapTransportMetricToRecordData(response.metric)
    },
    [invalidateRelatedQueries, updateMetricMutation],
  )

  // Tags-only partial update; never resends other fields so a tag edit can't
  // clobber a concurrent full-record save.
  const updateMetricTags = useCallback(
    async (input: {
      id: string
      tags: string[]
    }): Promise<PersistedMetricRecordData> => {
      const response = await updateMetricMutation.mutateAsync({
        metric_id: input.id,
        tags: input.tags,
      })
      await invalidateRelatedQueries()
      return mapTransportMetricToRecordData(response.metric)
    },
    [invalidateRelatedQueries, updateMetricMutation],
  )

  // Display-name-only partial update, for the same reason as the tags helper: a
  // rename from the list must not resend the rest of the record over a
  // concurrent full-record save.
  const updateMetricDisplayName = useCallback(
    async (input: {
      id: string
      metricDisplayName: string
    }): Promise<PersistedMetricRecordData> => {
      const response = await updateMetricMutation.mutateAsync({
        metric_id: input.id,
        metric_display_name: input.metricDisplayName,
      })
      await invalidateRelatedQueries()
      return mapTransportMetricToRecordData(response.metric)
    },
    [invalidateRelatedQueries, updateMetricMutation],
  )

  const archiveMetric = useCallback(
    async (request: ArchiveMetricRequest): Promise<void> => {
      await archiveMetricMutation.mutateAsync(request)
      await invalidateRelatedQueries()
    },
    [archiveMetricMutation, invalidateRelatedQueries],
  )

  const revertMetricVersion = useCallback(
    async (input: {
      metricId: string
      versionId: string
    }): Promise<PersistedMetricRecordData> => {
      const response = await revertMetricVersionMutation.mutateAsync({
        metric_id: input.metricId,
        version_id: input.versionId,
      })
      await invalidateRelatedQueries()
      return mapTransportMetricToRecordData(response.metric)
    },
    [invalidateRelatedQueries, revertMetricVersionMutation],
  )

  return {
    createMetric,
    duplicateMetric,
    updateMetric,
    updateMetricDisplayName,
    updateMetricTags,
    archiveMetric,
    revertMetricVersion,
    isCreating: createMetricMutation.isPending,
    isDuplicating: duplicateMetricMutation.isPending,
    isUpdating: updateMetricMutation.isPending,
    isArchiving: archiveMetricMutation.isPending,
    isReverting: revertMetricVersionMutation.isPending,
  }
}
